import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import MainLayout from '../../components/Layout/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { api } from '../../services/api';
import { ArrowBack, Save, Cancel } from '@mui/icons-material';
import * as MuiIcons from '@mui/icons-material';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  flex: 1;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const FormCard = styled(Card)`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
`;

const Input = styled.input`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const Select = styled.select`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
  cursor: pointer;
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ColorInput = styled.input`
  width: 100px;
  height: 40px;
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  cursor: pointer;
  background: none;
  padding: 0;
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  }
`;

const IconPreview = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary};
  font-size: 24px;
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const FormActions = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  justify-content: flex-end;
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const ErrorMessage = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.error}20;
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.error};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  color: ${props => getTheme(props.theme?.isDark).colors.error};
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const ConditionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const CustomerFunnelForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = { isDark };
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    icon: 'People',
    color: '#1976d2',
    order: 0,
    group: '',
    isActive: true,
    conditions: {
      status: [],
      hasActiveSubscription: null,
      hasAnySubscription: null,
      lastLoginDays: null,
      createdAtStart: '',
      createdAtEnd: '',
      hasWalkingLogs: null,
      walkingLogsDays: null,
      walkingStepsMin: null,
      walkingStepsMax: null,
      walkingStepsOperator: null,
      walkingTodayAtHour: null,
      hasWaterLogs: null,
      waterLogsDays: null,
      waterQuantityMin: null,
      waterQuantityMax: null,
      waterQuantityOperator: null,
      waterTodayAtHour: null,
      hasMealLogs: null,
      mealLogsDays: null,
      mealCountMin: null,
      mealCountMax: null,
      mealCountOperator: null,
      mealTodayAtHour: null,
      caloriesTodayMin: null,
      caloriesTodayMax: null,
      caloriesTodayOperator: null,
      caloriesExpression: null,
      hasWeightLogs: null,
      weightLogsDays: null,
      weightTodayAtHour: null,
      // Expression support
      waterExpression: null,
      walkingExpression: null,
      mealExpression: null,
    },
  });

  useEffect(() => {
    if (isEditMode) {
      fetchFunnel();
    }
  }, [id, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFunnel = async () => {
    try {
      setLoading(true);
      const funnel = await api.getCustomerFunnel(id);
      
      // Convert dates to YYYY-MM-DD format for input fields
      const conditions = { ...funnel.conditions };
      if (conditions.createdAtStart) {
        conditions.createdAtStart = new Date(conditions.createdAtStart).toISOString().split('T')[0];
      }
      if (conditions.createdAtEnd) {
        conditions.createdAtEnd = new Date(conditions.createdAtEnd).toISOString().split('T')[0];
      }
      // Ensure all log fields exist
      conditions.hasWalkingLogs = conditions.hasWalkingLogs !== undefined ? conditions.hasWalkingLogs : null;
      conditions.walkingLogsDays = conditions.walkingLogsDays || null;
      conditions.walkingStepsMin = conditions.walkingStepsMin || null;
      conditions.walkingStepsMax = conditions.walkingStepsMax || null;
      conditions.walkingStepsOperator = conditions.walkingStepsOperator || null;
      conditions.walkingTodayAtHour = conditions.walkingTodayAtHour !== undefined ? conditions.walkingTodayAtHour : null;
      conditions.hasWaterLogs = conditions.hasWaterLogs !== undefined ? conditions.hasWaterLogs : null;
      conditions.waterLogsDays = conditions.waterLogsDays || null;
      conditions.waterQuantityMin = conditions.waterQuantityMin || null;
      conditions.waterQuantityMax = conditions.waterQuantityMax || null;
      conditions.waterQuantityOperator = conditions.waterQuantityOperator || null;
      conditions.waterTodayAtHour = conditions.waterTodayAtHour !== undefined ? conditions.waterTodayAtHour : null;
      conditions.hasMealLogs = conditions.hasMealLogs !== undefined ? conditions.hasMealLogs : null;
      conditions.mealLogsDays = conditions.mealLogsDays || null;
      conditions.mealCountMin = conditions.mealCountMin || null;
      conditions.mealCountMax = conditions.mealCountMax || null;
      conditions.mealCountOperator = conditions.mealCountOperator || null;
      conditions.mealTodayAtHour = conditions.mealTodayAtHour !== undefined ? conditions.mealTodayAtHour : null;
      conditions.caloriesTodayMin = conditions.caloriesTodayMin || null;
      conditions.caloriesTodayMax = conditions.caloriesTodayMax || null;
      conditions.caloriesTodayOperator = conditions.caloriesTodayOperator || null;
      conditions.hasWeightLogs = conditions.hasWeightLogs !== undefined ? conditions.hasWeightLogs : null;
      conditions.weightLogsDays = conditions.weightLogsDays || null;
      conditions.weightTodayAtHour = conditions.weightTodayAtHour !== undefined ? conditions.weightTodayAtHour : null;
      // Load expressions
      conditions.waterExpression = conditions.waterExpression || null;
      conditions.caloriesExpression = conditions.caloriesExpression || null;
      conditions.walkingExpression = conditions.walkingExpression || null;
      conditions.mealExpression = conditions.mealExpression || null;
      
      setFormData({
        name: funnel.name || '',
        icon: funnel.icon || 'People',
        color: funnel.color || '#1976d2',
        order: funnel.order || 0,
        group: funnel.group || '',
        isActive: funnel.isActive !== undefined ? funnel.isActive : true,
        conditions,
      });
    } catch (err) {
      console.error('Error fetching funnel:', err);
      setError('Failed to load funnel data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConditionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: value,
      },
    }));
  };

  const handleStatusToggle = (status) => {
    setFormData(prev => {
      const currentStatus = prev.conditions.status || [];
      const newStatus = currentStatus.includes(status)
        ? currentStatus.filter(s => s !== status)
        : [...currentStatus, status];
      return {
        ...prev,
        conditions: {
          ...prev.conditions,
          status: newStatus,
        },
      };
    });
  };

  const handleSubscriptionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: value === '' ? null : value === 'true',
        // Clear the other subscription field when one is set
        ...(field === 'hasActiveSubscription' ? { hasAnySubscription: null } : {}),
        ...(field === 'hasAnySubscription' ? { hasActiveSubscription: null } : {}),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Prepare data for submission
      const submitData = {
        name: formData.name,
        icon: formData.icon,
        color: formData.color,
        order: parseInt(formData.order) || 0,
        group: formData.group || null,
        isActive: formData.isActive,
        conditions: {
          status: formData.conditions.status || [],
          hasActiveSubscription: formData.conditions.hasActiveSubscription === '' ? null : formData.conditions.hasActiveSubscription,
          hasAnySubscription: formData.conditions.hasAnySubscription === '' ? null : formData.conditions.hasAnySubscription,
          lastLoginDays: formData.conditions.lastLoginDays ? parseInt(formData.conditions.lastLoginDays) : null,
          createdAtStart: formData.conditions.createdAtStart ? new Date(formData.conditions.createdAtStart).toISOString() : null,
          createdAtEnd: formData.conditions.createdAtEnd ? new Date(formData.conditions.createdAtEnd).toISOString() : null,
          hasWalkingLogs: formData.conditions.hasWalkingLogs === '' ? null : formData.conditions.hasWalkingLogs,
          walkingLogsDays: formData.conditions.walkingLogsDays ? parseInt(formData.conditions.walkingLogsDays) : null,
          walkingStepsMin: formData.conditions.walkingStepsMin ? parseInt(formData.conditions.walkingStepsMin) : null,
          walkingStepsMax: formData.conditions.walkingStepsMax ? parseInt(formData.conditions.walkingStepsMax) : null,
          walkingStepsOperator: formData.conditions.walkingStepsOperator || null,
          walkingTodayAtHour: formData.conditions.walkingTodayAtHour !== '' && formData.conditions.walkingTodayAtHour !== null ? parseInt(formData.conditions.walkingTodayAtHour) : null,
          hasWaterLogs: formData.conditions.hasWaterLogs === '' ? null : formData.conditions.hasWaterLogs,
          waterLogsDays: formData.conditions.waterLogsDays ? parseInt(formData.conditions.waterLogsDays) : null,
          waterQuantityMin: formData.conditions.waterQuantityMin ? parseInt(formData.conditions.waterQuantityMin) : null,
          waterQuantityMax: formData.conditions.waterQuantityMax ? parseInt(formData.conditions.waterQuantityMax) : null,
          waterQuantityOperator: formData.conditions.waterQuantityOperator || null,
          waterExpression: formData.conditions.waterExpression || null,
          waterTodayAtHour: formData.conditions.waterTodayAtHour !== '' && formData.conditions.waterTodayAtHour !== null ? parseInt(formData.conditions.waterTodayAtHour) : null,
          hasMealLogs: formData.conditions.hasMealLogs === '' ? null : formData.conditions.hasMealLogs,
          mealLogsDays: formData.conditions.mealLogsDays ? parseInt(formData.conditions.mealLogsDays) : null,
          mealCountMin: formData.conditions.mealCountMin ? parseInt(formData.conditions.mealCountMin) : null,
          mealCountMax: formData.conditions.mealCountMax ? parseInt(formData.conditions.mealCountMax) : null,
          mealCountOperator: formData.conditions.mealCountOperator || null,
          mealTodayAtHour: formData.conditions.mealTodayAtHour !== '' && formData.conditions.mealTodayAtHour !== null ? parseInt(formData.conditions.mealTodayAtHour) : null,
          caloriesTodayMin: formData.conditions.caloriesTodayMin ? parseInt(formData.conditions.caloriesTodayMin) : null,
          caloriesTodayMax: formData.conditions.caloriesTodayMax ? parseInt(formData.conditions.caloriesTodayMax) : null,
          caloriesTodayOperator: formData.conditions.caloriesTodayOperator || null,
          caloriesExpression: formData.conditions.caloriesExpression || null,
          hasWeightLogs: formData.conditions.hasWeightLogs === '' ? null : formData.conditions.hasWeightLogs,
          weightLogsDays: formData.conditions.weightLogsDays ? parseInt(formData.conditions.weightLogsDays) : null,
          weightTodayAtHour: formData.conditions.weightTodayAtHour !== '' && formData.conditions.weightTodayAtHour !== null ? parseInt(formData.conditions.weightTodayAtHour) : null,
        },
      };

      if (isEditMode) {
        await api.updateCustomerFunnel(id, submitData);
      } else {
        await api.createCustomerFunnel(submitData);
      }

      navigate('/admin/customerFunnels');
    } catch (err) {
      console.error('Error saving funnel:', err);
      setError(err.response?.data?.message || 'Failed to save funnel');
    } finally {
      setSaving(false);
    }
  };

  // Get available Material-UI icons
  const availableIcons = [
    'People', 'Person', 'PersonAdd', 'CheckCircle', 'Cancel', 'Block',
    'CreditCard', 'TrendingDown', 'TrendingUp', 'Star', 'AccessTime',
    'CalendarToday', 'Email', 'Phone', 'LocationOn', 'Settings',
    'Dashboard', 'Analytics', 'BarChart', 'PieChart', 'Timeline',
    'DirectionsWalk', 'WaterDrop', 'Restaurant', 'Scale', 'FitnessCenter',
  ];

  const getIconComponent = (iconName) => {
    const IconComponent = MuiIcons[iconName] || MuiIcons.People;
    return IconComponent;
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingContainer theme={theme}>Loading funnel...</LoadingContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <Header>
          <BackButton onClick={() => navigate('/admin/customerFunnels')} theme={theme} variant="outline">
            <ArrowBack /> Back
          </BackButton>
          <Title theme={theme}>
            {isEditMode ? 'Edit Customer Funnel' : 'Create Customer Funnel'}
          </Title>
        </Header>

        <FormCard theme={theme}>
          <Form onSubmit={handleSubmit}>
            {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}

            <FormGroup theme={theme}>
              <Label theme={theme}>Name *</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Total Customers"
                required
                theme={theme}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Icon</Label>
              <Select
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                theme={theme}
              >
                {availableIcons.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </Select>
              <IconPreview theme={theme} color={formData.color}>
                {React.createElement(getIconComponent(formData.icon))}
              </IconPreview>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Color</Label>
              <div style={{ display: 'flex', gap: getTheme(isDark).spacing.md, alignItems: 'center' }}>
                <ColorInput
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  theme={theme}
                />
                <Input
                  type="text"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  placeholder="#1976d2"
                  pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                  theme={theme}
                  style={{ flex: 1 }}
                />
              </div>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => handleInputChange('order', e.target.value)}
                placeholder="0"
                min="0"
                theme={theme}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Group</Label>
              <Input
                type="text"
                value={formData.group}
                onChange={(e) => handleInputChange('group', e.target.value)}
                placeholder="e.g., Overview, Engagement, Monetization"
                theme={theme}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <CheckboxLabel theme={theme}>
                <Checkbox
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
                Active
              </CheckboxLabel>
            </FormGroup>

            <ConditionsSection theme={theme}>
              <SectionTitle theme={theme}>Conditions</SectionTitle>

              <FormGroup theme={theme}>
                <Label theme={theme}>Status</Label>
                <CheckboxGroup theme={theme}>
                  {['incomplete', 'completed', 'active', 'banned'].map(status => (
                    <CheckboxLabel key={status} theme={theme}>
                      <Checkbox
                        type="checkbox"
                        checked={formData.conditions.status?.includes(status)}
                        onChange={() => handleStatusToggle(status)}
                      />
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </CheckboxLabel>
                  ))}
                </CheckboxGroup>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Has Active Subscription</Label>
                <Select
                  value={formData.conditions.hasActiveSubscription === null ? '' : String(formData.conditions.hasActiveSubscription)}
                  onChange={(e) => handleSubscriptionChange('hasActiveSubscription', e.target.value)}
                  theme={theme}
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Has Any Subscription</Label>
                <Select
                  value={formData.conditions.hasAnySubscription === null ? '' : String(formData.conditions.hasAnySubscription)}
                  onChange={(e) => handleSubscriptionChange('hasAnySubscription', e.target.value)}
                  theme={theme}
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Last Login (Days)</Label>
                <Input
                  type="number"
                  value={formData.conditions.lastLoginDays || ''}
                  onChange={(e) => handleConditionChange('lastLoginDays', e.target.value || null)}
                  placeholder="e.g., 30 for last 30 days"
                  min="0"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Created At Start</Label>
                <Input
                  type="date"
                  value={formData.conditions.createdAtStart || ''}
                  onChange={(e) => handleConditionChange('createdAtStart', e.target.value)}
                  theme={theme}
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Created At End</Label>
                <Input
                  type="date"
                  value={formData.conditions.createdAtEnd || ''}
                  onChange={(e) => handleConditionChange('createdAtEnd', e.target.value)}
                  theme={theme}
                />
              </FormGroup>
            </ConditionsSection>

            <ConditionsSection theme={theme}>
              <SectionTitle theme={theme}>Log Filters</SectionTitle>

              <FormGroup theme={theme}>
                <Label theme={theme}>Walking Logs</Label>
                <Select
                  value={formData.conditions.hasWalkingLogs === null ? '' : String(formData.conditions.hasWalkingLogs)}
                  onChange={(e) => handleConditionChange('hasWalkingLogs', e.target.value === '' ? null : e.target.value === 'true')}
                  theme={theme}
                >
                  <option value="">Any</option>
                  <option value="true">Has Walking Logs</option>
                  <option value="false">No Walking Logs</option>
                </Select>
                {formData.conditions.hasWalkingLogs !== null && (
                  <>
                    <Input
                      type="number"
                      value={formData.conditions.walkingLogsDays || ''}
                      onChange={(e) => handleConditionChange('walkingLogsDays', e.target.value || null)}
                      placeholder="Days (e.g., 7 for last 7 days)"
                      min="0"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                    <Select
                      value={formData.conditions.walkingStepsOperator || ''}
                      onChange={(e) => handleConditionChange('walkingStepsOperator', e.target.value || null)}
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    >
                      <option value="">No step filter</option>
                      <option value="less">Less than</option>
                      <option value="more">More than</option>
                      <option value="between">Between</option>
                      <option value="exact">Exactly</option>
                    </Select>
                    {formData.conditions.walkingStepsOperator && (
                      <>
                        <Input
                          type="number"
                          value={formData.conditions.walkingStepsMin || ''}
                          onChange={(e) => handleConditionChange('walkingStepsMin', e.target.value || null)}
                          placeholder="Min steps"
                          min="0"
                          theme={theme}
                          style={{ marginTop: getTheme(isDark).spacing.sm }}
                        />
                        {(formData.conditions.walkingStepsOperator === 'between') && (
                          <Input
                            type="number"
                            value={formData.conditions.walkingStepsMax || ''}
                            onChange={(e) => handleConditionChange('walkingStepsMax', e.target.value || null)}
                            placeholder="Max steps"
                            min="0"
                            theme={theme}
                            style={{ marginTop: getTheme(isDark).spacing.sm }}
                          />
                        )}
                      </>
                    )}
                    <Input
                      type="number"
                      value={formData.conditions.walkingTodayAtHour !== null ? formData.conditions.walkingTodayAtHour : ''}
                      onChange={(e) => handleConditionChange('walkingTodayAtHour', e.target.value !== '' ? parseInt(e.target.value) : null)}
                      placeholder="Today at hour (0-23, empty for any)"
                      min="0"
                      max="23"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                  </>
                )}
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Water Logs</Label>
                <Select
                  value={formData.conditions.hasWaterLogs === null ? '' : String(formData.conditions.hasWaterLogs)}
                  onChange={(e) => handleConditionChange('hasWaterLogs', e.target.value === '' ? null : e.target.value === 'true')}
                  theme={theme}
                >
                  <option value="">Any</option>
                  <option value="true">Has Water Logs</option>
                  <option value="false">No Water Logs</option>
                </Select>
                {formData.conditions.hasWaterLogs !== null && (
                  <>
                    <Input
                      type="number"
                      value={formData.conditions.waterLogsDays || ''}
                      onChange={(e) => handleConditionChange('waterLogsDays', e.target.value || null)}
                      placeholder="Days (e.g., 7 for last 7 days)"
                      min="0"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                    <Select
                      value={formData.conditions.waterQuantityOperator || ''}
                      onChange={(e) => handleConditionChange('waterQuantityOperator', e.target.value || null)}
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    >
                      <option value="">No quantity filter</option>
                      <option value="less">Less than</option>
                      <option value="more">More than</option>
                      <option value="between">Between</option>
                      <option value="exact">Exactly</option>
                    </Select>
                    {formData.conditions.waterQuantityOperator && (
                      <>
                        <Input
                          type="number"
                          value={formData.conditions.waterQuantityMin || ''}
                          onChange={(e) => handleConditionChange('waterQuantityMin', e.target.value || null)}
                          placeholder="Min quantity (ml)"
                          min="0"
                          theme={theme}
                          style={{ marginTop: getTheme(isDark).spacing.sm }}
                        />
                        {(formData.conditions.waterQuantityOperator === 'between') && (
                          <Input
                            type="number"
                            value={formData.conditions.waterQuantityMax || ''}
                            onChange={(e) => handleConditionChange('waterQuantityMax', e.target.value || null)}
                            placeholder="Max quantity (ml)"
                            min="0"
                            theme={theme}
                            style={{ marginTop: getTheme(isDark).spacing.sm }}
                          />
                        )}
                      </>
                    )}
                    <Input
                      type="number"
                      value={formData.conditions.waterTodayAtHour !== null ? formData.conditions.waterTodayAtHour : ''}
                      onChange={(e) => handleConditionChange('waterTodayAtHour', e.target.value !== '' ? parseInt(e.target.value) : null)}
                      placeholder="Today at hour (0-23, empty for any)"
                      min="0"
                      max="23"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                  </>
                )}
                {/* Expression-based water condition */}
                <div style={{ marginTop: getTheme(isDark).spacing.md, padding: getTheme(isDark).spacing.md, border: `1px dashed ${getTheme(isDark).colors.border}`, borderRadius: getTheme(isDark).borderRadius.md }}>
                  <Label theme={theme} style={{ marginBottom: getTheme(isDark).spacing.sm }}>Expression-based Condition (e.g., todayDrunkWater {'<'} waterGoal/4)</Label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: getTheme(isDark).spacing.sm }}>
                    <Select
                      value={formData.conditions.waterExpression?.variable || ''}
                      onChange={(e) => {
                        const expr = formData.conditions.waterExpression || {};
                        handleConditionChange('waterExpression', e.target.value ? { ...expr, variable: e.target.value } : null);
                      }}
                      theme={theme}
                    >
                      <option value="">No expression</option>
                      <option value="todayDrunkWater">Bu günkü içilən su</option>
                      <option value="waterGoal">Gündəlik su hədəfi</option>
                      <option value="remainingWater">Qalan su</option>
                    </Select>
                    {formData.conditions.waterExpression?.variable && (
                      <>
                        <Select
                          value={formData.conditions.waterExpression?.operator || ''}
                          onChange={(e) => {
                            const expr = formData.conditions.waterExpression || {};
                            handleConditionChange('waterExpression', e.target.value ? { ...expr, operator: e.target.value } : null);
                          }}
                          theme={theme}
                        >
                          <option value="">Operator seçin</option>
                          <option value="<">{'<'}</option>
                          <option value="<=">{'<='}</option>
                          <option value=">">{'>'}</option>
                          <option value=">=">{'>='}</option>
                          <option value="==">==</option>
                          <option value="!=">!=</option>
                        </Select>
                        {formData.conditions.waterExpression?.operator && (
                          <>
                            <Label theme={theme}>Müqayisə tipi:</Label>
                            <Select
                              value={formData.conditions.waterExpression?.comparisonValue?.type || ''}
                              onChange={(e) => {
                                const expr = formData.conditions.waterExpression || {};
                                const compVal = { type: e.target.value };
                                handleConditionChange('waterExpression', { ...expr, comparisonValue: compVal });
                              }}
                              theme={theme}
                            >
                              <option value="">Tip seçin</option>
                              <option value="constant">Sabit dəyər</option>
                              <option value="variable">Dəyişən</option>
                              <option value="expression">İfadə (dəyişən + əməliyyat)</option>
                            </Select>
                            {formData.conditions.waterExpression?.comparisonValue?.type === 'constant' && (
                              <Input
                                type="number"
                                value={formData.conditions.waterExpression?.comparisonValue?.constant || ''}
                                onChange={(e) => {
                                  const expr = formData.conditions.waterExpression || {};
                                  const compVal = { ...expr.comparisonValue, constant: parseFloat(e.target.value) || null };
                                  handleConditionChange('waterExpression', { ...expr, comparisonValue: compVal });
                                }}
                                placeholder="Sabit dəyər"
                                theme={theme}
                              />
                            )}
                            {formData.conditions.waterExpression?.comparisonValue?.type === 'variable' && (
                              <Select
                                value={formData.conditions.waterExpression?.comparisonValue?.variable || ''}
                                onChange={(e) => {
                                  const expr = formData.conditions.waterExpression || {};
                                  const compVal = { ...expr.comparisonValue, variable: e.target.value };
                                  handleConditionChange('waterExpression', { ...expr, comparisonValue: compVal });
                                }}
                                theme={theme}
                              >
                                <option value="">Dəyişən seçin</option>
                                <option value="todayDrunkWater">Bu günkü içilən su</option>
                                <option value="waterGoal">Gündəlik su hədəfi</option>
                                <option value="remainingWater">Qalan su</option>
                              </Select>
                            )}
                            {formData.conditions.waterExpression?.comparisonValue?.type === 'expression' && (
                              <>
                                <Select
                                  value={formData.conditions.waterExpression?.comparisonValue?.expression?.variable || ''}
                                  onChange={(e) => {
                                    const expr = formData.conditions.waterExpression || {};
                                    const compExpr = { ...expr.comparisonValue.expression, variable: e.target.value };
                                    const compVal = { ...expr.comparisonValue, expression: compExpr };
                                    handleConditionChange('waterExpression', { ...expr, comparisonValue: compVal });
                                  }}
                                  theme={theme}
                                >
                                  <option value="">Dəyişən seçin</option>
                                  <option value="todayDrunkWater">Bu günkü içilən su</option>
                                  <option value="waterGoal">Gündəlik su hədəfi</option>
                                  <option value="remainingWater">Qalan su</option>
                                </Select>
                                <Select
                                  value={formData.conditions.waterExpression?.comparisonValue?.expression?.operation || ''}
                                  onChange={(e) => {
                                    const expr = formData.conditions.waterExpression || {};
                                    const compExpr = { ...expr.comparisonValue.expression, operation: e.target.value };
                                    const compVal = { ...expr.comparisonValue, expression: compExpr };
                                    handleConditionChange('waterExpression', { ...expr, comparisonValue: compVal });
                                  }}
                                  theme={theme}
                                >
                                  <option value="">Əməliyyat seçin</option>
                                  <option value="+">+ (Toplama)</option>
                                  <option value="-">- (Çıxma)</option>
                                  <option value="*">× (Vurma)</option>
                                  <option value="/">÷ (Bölmə)</option>
                                </Select>
                                <Input
                                  type="text"
                                  value={formData.conditions.waterExpression?.comparisonValue?.expression?.value || ''}
                                  onChange={(e) => {
                                    const expr = formData.conditions.waterExpression || {};
                                    const val = isNaN(e.target.value) ? e.target.value : parseFloat(e.target.value);
                                    const compExpr = { ...expr.comparisonValue.expression, value: val };
                                    const compVal = { ...expr.comparisonValue, expression: compExpr };
                                    handleConditionChange('waterExpression', { ...expr, comparisonValue: compVal });
                                  }}
                                  placeholder="Dəyər və ya dəyişən adı (məs: 4 və ya waterGoal)"
                                  theme={theme}
                                />
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Meal Logs</Label>
                <Select
                  value={formData.conditions.hasMealLogs === null ? '' : String(formData.conditions.hasMealLogs)}
                  onChange={(e) => handleConditionChange('hasMealLogs', e.target.value === '' ? null : e.target.value === 'true')}
                  theme={theme}
                >
                  <option value="">Any</option>
                  <option value="true">Has Meal Logs</option>
                  <option value="false">No Meal Logs</option>
                </Select>
                {formData.conditions.hasMealLogs !== null && (
                  <>
                    <Input
                      type="number"
                      value={formData.conditions.mealLogsDays || ''}
                      onChange={(e) => handleConditionChange('mealLogsDays', e.target.value || null)}
                      placeholder="Days (e.g., 7 for last 7 days)"
                      min="0"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                    <Select
                      value={formData.conditions.mealCountOperator || ''}
                      onChange={(e) => handleConditionChange('mealCountOperator', e.target.value || null)}
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    >
                      <option value="">No meal count filter</option>
                      <option value="less">Less than</option>
                      <option value="more">More than</option>
                      <option value="between">Between</option>
                      <option value="exact">Exactly</option>
                    </Select>
                    {formData.conditions.mealCountOperator && (
                      <>
                        <Input
                          type="number"
                          value={formData.conditions.mealCountMin || ''}
                          onChange={(e) => handleConditionChange('mealCountMin', e.target.value || null)}
                          placeholder="Min meal count"
                          min="0"
                          theme={theme}
                          style={{ marginTop: getTheme(isDark).spacing.sm }}
                        />
                        {(formData.conditions.mealCountOperator === 'between') && (
                          <Input
                            type="number"
                            value={formData.conditions.mealCountMax || ''}
                            onChange={(e) => handleConditionChange('mealCountMax', e.target.value || null)}
                            placeholder="Max meal count"
                            min="0"
                            theme={theme}
                            style={{ marginTop: getTheme(isDark).spacing.sm }}
                          />
                        )}
                      </>
                    )}
                    <Input
                      type="number"
                      value={formData.conditions.mealTodayAtHour !== null ? formData.conditions.mealTodayAtHour : ''}
                      onChange={(e) => handleConditionChange('mealTodayAtHour', e.target.value !== '' ? parseInt(e.target.value) : null)}
                      placeholder="Today at hour (0-23, empty for any)"
                      min="0"
                      max="23"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                  </>
                )}
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Calories Today</Label>
                <Select
                  value={formData.conditions.caloriesTodayOperator || ''}
                  onChange={(e) => handleConditionChange('caloriesTodayOperator', e.target.value || null)}
                  theme={theme}
                >
                  <option value="">No calorie filter</option>
                  <option value="less">Less than</option>
                  <option value="more">More than</option>
                  <option value="between">Between</option>
                  <option value="exact">Exactly</option>
                </Select>
                {formData.conditions.caloriesTodayOperator && (
                  <>
                    <Input
                      type="number"
                      value={formData.conditions.caloriesTodayMin || ''}
                      onChange={(e) => handleConditionChange('caloriesTodayMin', e.target.value || null)}
                      placeholder="Min calories"
                      min="0"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                    {(formData.conditions.caloriesTodayOperator === 'between') && (
                      <Input
                        type="number"
                        value={formData.conditions.caloriesTodayMax || ''}
                        onChange={(e) => handleConditionChange('caloriesTodayMax', e.target.value || null)}
                        placeholder="Max calories"
                        min="0"
                        theme={theme}
                        style={{ marginTop: getTheme(isDark).spacing.sm }}
                      />
                    )}
                  </>
                )}
                {/* Expression-based calories condition */}
                <div style={{ marginTop: getTheme(isDark).spacing.md, padding: getTheme(isDark).spacing.md, border: `1px dashed ${getTheme(isDark).colors.border}`, borderRadius: getTheme(isDark).borderRadius.md }}>
                  <Label theme={theme} style={{ marginBottom: getTheme(isDark).spacing.sm }}>Expression-based Condition</Label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: getTheme(isDark).spacing.sm }}>
                    <Select
                      value={formData.conditions.caloriesExpression?.variable || ''}
                      onChange={(e) => {
                        const expr = formData.conditions.caloriesExpression || {};
                        handleConditionChange('caloriesExpression', e.target.value ? { ...expr, variable: e.target.value } : null);
                      }}
                      theme={theme}
                    >
                      <option value="">No expression</option>
                      <option value="todayCalories">Bu günkü kalori</option>
                      <option value="calorieGoal">Gündəlik kalori hədəfi</option>
                      <option value="remainingCalories">Qalan kalori</option>
                    </Select>
                    {formData.conditions.caloriesExpression?.variable && (
                      <>
                        <Select
                          value={formData.conditions.caloriesExpression?.operator || ''}
                          onChange={(e) => {
                            const expr = formData.conditions.caloriesExpression || {};
                            handleConditionChange('caloriesExpression', e.target.value ? { ...expr, operator: e.target.value } : null);
                          }}
                          theme={theme}
                        >
                          <option value="">Operator seçin</option>
                          <option value="<">{'<'}</option>
                          <option value="<=">{'<='}</option>
                          <option value=">">{'>'}</option>
                          <option value=">=">{'>='}</option>
                          <option value="==">==</option>
                          <option value="!=">!=</option>
                        </Select>
                        {formData.conditions.caloriesExpression?.operator && (
                          <>
                            <Label theme={theme}>Müqayisə tipi:</Label>
                            <Select
                              value={formData.conditions.caloriesExpression?.comparisonValue?.type || ''}
                              onChange={(e) => {
                                const expr = formData.conditions.caloriesExpression || {};
                                const compVal = { type: e.target.value };
                                handleConditionChange('caloriesExpression', { ...expr, comparisonValue: compVal });
                              }}
                              theme={theme}
                            >
                              <option value="">Tip seçin</option>
                              <option value="constant">Sabit dəyər</option>
                              <option value="variable">Dəyişən</option>
                              <option value="expression">İfadə (dəyişən + əməliyyat)</option>
                            </Select>
                            {formData.conditions.caloriesExpression?.comparisonValue?.type === 'constant' && (
                              <Input
                                type="number"
                                value={formData.conditions.caloriesExpression?.comparisonValue?.constant || ''}
                                onChange={(e) => {
                                  const expr = formData.conditions.caloriesExpression || {};
                                  const compVal = { ...expr.comparisonValue, constant: parseFloat(e.target.value) || null };
                                  handleConditionChange('caloriesExpression', { ...expr, comparisonValue: compVal });
                                }}
                                placeholder="Sabit dəyər"
                                theme={theme}
                              />
                            )}
                            {formData.conditions.caloriesExpression?.comparisonValue?.type === 'variable' && (
                              <Select
                                value={formData.conditions.caloriesExpression?.comparisonValue?.variable || ''}
                                onChange={(e) => {
                                  const expr = formData.conditions.caloriesExpression || {};
                                  const compVal = { ...expr.comparisonValue, variable: e.target.value };
                                  handleConditionChange('caloriesExpression', { ...expr, comparisonValue: compVal });
                                }}
                                theme={theme}
                              >
                                <option value="">Dəyişən seçin</option>
                                <option value="todayCalories">Bu günkü kalori</option>
                                <option value="calorieGoal">Gündəlik kalori hədəfi</option>
                                <option value="remainingCalories">Qalan kalori</option>
                              </Select>
                            )}
                            {formData.conditions.caloriesExpression?.comparisonValue?.type === 'expression' && (
                              <>
                                <Select
                                  value={formData.conditions.caloriesExpression?.comparisonValue?.expression?.variable || ''}
                                  onChange={(e) => {
                                    const expr = formData.conditions.caloriesExpression || {};
                                    const compExpr = { ...expr.comparisonValue.expression, variable: e.target.value };
                                    const compVal = { ...expr.comparisonValue, expression: compExpr };
                                    handleConditionChange('caloriesExpression', { ...expr, comparisonValue: compVal });
                                  }}
                                  theme={theme}
                                >
                                  <option value="">Dəyişən seçin</option>
                                  <option value="todayCalories">Bu günkü kalori</option>
                                  <option value="calorieGoal">Gündəlik kalori hədəfi</option>
                                  <option value="remainingCalories">Qalan kalori</option>
                                </Select>
                                <Select
                                  value={formData.conditions.caloriesExpression?.comparisonValue?.expression?.operation || ''}
                                  onChange={(e) => {
                                    const expr = formData.conditions.caloriesExpression || {};
                                    const compExpr = { ...expr.comparisonValue.expression, operation: e.target.value };
                                    const compVal = { ...expr.comparisonValue, expression: compExpr };
                                    handleConditionChange('caloriesExpression', { ...expr, comparisonValue: compVal });
                                  }}
                                  theme={theme}
                                >
                                  <option value="">Əməliyyat seçin</option>
                                  <option value="+">+ (Toplama)</option>
                                  <option value="-">- (Çıxma)</option>
                                  <option value="*">× (Vurma)</option>
                                  <option value="/">÷ (Bölmə)</option>
                                </Select>
                                <Input
                                  type="text"
                                  value={formData.conditions.caloriesExpression?.comparisonValue?.expression?.value || ''}
                                  onChange={(e) => {
                                    const expr = formData.conditions.caloriesExpression || {};
                                    const val = isNaN(e.target.value) ? e.target.value : parseFloat(e.target.value);
                                    const compExpr = { ...expr.comparisonValue.expression, value: val };
                                    const compVal = { ...expr.comparisonValue, expression: compExpr };
                                    handleConditionChange('caloriesExpression', { ...expr, comparisonValue: compVal });
                                  }}
                                  placeholder="Dəyər və ya dəyişən adı (məs: 4 və ya calorieGoal)"
                                  theme={theme}
                                />
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Weight Logs</Label>
                <Select
                  value={formData.conditions.hasWeightLogs === null ? '' : String(formData.conditions.hasWeightLogs)}
                  onChange={(e) => handleConditionChange('hasWeightLogs', e.target.value === '' ? null : e.target.value === 'true')}
                  theme={theme}
                >
                  <option value="">Any</option>
                  <option value="true">Has Weight Logs</option>
                  <option value="false">No Weight Logs</option>
                </Select>
                {formData.conditions.hasWeightLogs !== null && (
                  <>
                    <Input
                      type="number"
                      value={formData.conditions.weightLogsDays || ''}
                      onChange={(e) => handleConditionChange('weightLogsDays', e.target.value || null)}
                      placeholder="Days (e.g., 7 for last 7 days)"
                      min="0"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                    <Input
                      type="number"
                      value={formData.conditions.weightTodayAtHour !== null ? formData.conditions.weightTodayAtHour : ''}
                      onChange={(e) => handleConditionChange('weightTodayAtHour', e.target.value !== '' ? parseInt(e.target.value) : null)}
                      placeholder="Today at hour (0-23, empty for any)"
                      min="0"
                      max="23"
                      theme={theme}
                      style={{ marginTop: getTheme(isDark).spacing.sm }}
                    />
                  </>
                )}
              </FormGroup>
            </ConditionsSection>

            <FormActions theme={theme}>
              <Button
                type="button"
                onClick={() => navigate('/admin/customerFunnels')}
                theme={theme}
                variant="outline"
              >
                <Cancel /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                theme={theme}
              >
                <Save /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </FormActions>
          </Form>
        </FormCard>
      </PageContainer>
    </MainLayout>
  );
};

export default CustomerFunnelForm;

